import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(
        'min-h-11 w-full rounded-control border border-line bg-surface px-3.5 text-base text-fg placeholder:text-muted',
        'focus:border-accent aria-[invalid=true]:border-negative',
        className,
      )}
      {...rest}
    />
  );
});

interface FieldProps {
  label: string;
  error?: string | undefined;
  hint?: string;
  children: (props: { id: string; 'aria-invalid': boolean; 'aria-describedby': string | undefined }) => ReactNode;
}

/** Label + control + error text wired together for screen readers. */
export function Field({ label, error, hint, children }: FieldProps) {
  const id = useId();
  const msgId = `${id}-msg`;
  const hasMsg = Boolean(error ?? hint);
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-fg">
        {label}
      </label>
      {children({ id, 'aria-invalid': Boolean(error), 'aria-describedby': hasMsg ? msgId : undefined })}
      {hasMsg && (
        <p id={msgId} className={cn('text-sm', error ? 'text-negative' : 'text-muted')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
